<template>
  <div class="product-description">
    <div class="description-spacing">
      <a
        :href="productUrl"
        :title="productName"
        :aria-label="productName"
        :class="['brand-and-name', { clickAction: clickAction === 'fireEvent' }]"
        target="_self"
        @click="fireEvent"
      >
        <div v-if="brandName" :class="['product-brand', brandFontClass]">
          {{ brandName }}
        </div>
        <div v-if="productName" :class="['product-name', productNameClass]">
          {{ productName }}
        </div>
      </a>
    </div>

    <div class="expand pricing-and-reviewlet">
      <div v-if="marketPlaceSellerName">Shipped and sold by {{ marketPlaceSellerName }}</div>

      <div v-if="productDetail?.colorAndSize?.length" class="product-size-color">
        <div v-for="item in productDetail.colorAndSize" :key="item">
          {{ item }}
        </div>
      </div>

      <div v-if="productDetail?.quantity && productDetail?.quantity > 1" class="product-qty">
        <span>Qty: {{ productDetail.quantity }}</span>
      </div>

      <div v-if="productDetail?.giftCardEmail" class="margin-top-xs">
        {{ productDetail.giftCardEmail }}
      </div>

      <PricingBlock :pricing="product?.pricing?.price" :class="cssMarginSimplification" />

      <div v-if="product?.withProtectionPlan" class="protection-plan grid-y margin-top-xs">
        <p v-if="product?.planProviderName" class="protection-plan-provider">
          {{ product.planProviderName }}
        </p>
        <p v-if="product?.planDesc" class="protection-plan-desc margin-bottom-xxs">
          {{ product.planDesc }}
        </p>
        <p v-if="product?.protectionPlanPrice" class="protection-plan-price margin-bottom-xxs">
          {{ product.protectionPlanPrice }}
        </p>
        <p v-if="product?.protectionPlanStatus" class="protection-plan-status margin-bottom-xxs">
          {{ product.protectionPlanStatus }}
        </p>
        <p v-if="product?.protectionPlanLabel" class="protection-plan-label">
          {{ product.protectionPlanLabel }}
        </p>
        <a v-if="isWNMPlanStatusViewEnabled" href="#" class="link-body" @click.prevent="openProtectionPlanDetails">{{
          PROTECTION_PLAN_SLIDEOUT.view_details
        }}</a>
      </div>

      <div v-if="product?.returnWindowDate" class="product-return-window">
        <span>Return by {{ product?.returnWindowDate }}</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, defineAsyncComponent, markRaw, onUnmounted } from 'vue';
import { decode } from '@vue-util/entities';
import PricingBlock from './PricingBlock';
import useBrand from '@/composables/useBrand';
import { useOrderDetailsStore } from '@/stores/useOrderDetailsStore';
import { useProtectionPlanStore } from '@/stores/ppe/useProtectionPlanStore';
import { useSlideoutStore } from '@nuxt-common/atomic-ui/app/stores/useSlideoutStore';
import { PROTECTION_PLAN_SLIDEOUT } from 'config';
import { SAFE_URL_PATTERN } from '#shared/constants';

const { isBcom } = useBrand();

const props = defineProps({
  product: {
    type: Object,
    default: () => ({}),
  },
  clickAction: {
    type: String,
    default: 'linkToPdp',
  },
  url: {
    type: String,
    default: '',
  },
  mcomFontWeight: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['getMoreDetails']);

const PROTECTION_PLAN_SLIDER_ID = 'protection-plan-slideout';

const slideoutStore = useSlideoutStore();
const orderDetailsStore = useOrderDetailsStore();
const protectionPlanStore = useProtectionPlanStore();

const isWNMPlanStatusViewEnabled = computed(() => {
  const planStatus = props.product?.protectionPlanStatus?.toLowerCase();
  const SUPPRESSED_PLAN_STATUSES = new Set(['plan canceled', 'pending']);
  return !!(orderDetailsStore.killSwitch?.wnmPlanStatusViewEnabled && props.product?.serviceLineId && !SUPPRESSED_PLAN_STATUSES.has(planStatus));
});

const productUrl = computed(() => {
  const url = props.url;
  return url && SAFE_URL_PATTERN.test(url) ? url : undefined;
});

const productDetail = computed(() => props.product?.detail);

const marketPlaceSellerName = computed(() => props.product?.mkpLineItemDetails?.sellerName);

const brandName = computed(() => {
  const detail = productDetail.value;
  const brand = detail?.subBrand || detail?.brand?.name || detail?.brand || '';
  return decode(brand);
});

const productName = computed(() => {
  const detail = productDetail.value;
  const name = detail?.productName || detail?.name || '';
  return decode(name);
});

const cssMarginSimplification = computed(() => (isBcom ? 'ps-margin-A' : 'ps-margin-B'));

const brandFontClass = computed(() => (isBcom || props.mcomFontWeight ? 'heavy' : 'medium'));

const productNameClass = computed(() => (isBcom || props.mcomFontWeight ? 'h3-exp-style' : 'medium'));

const fireEvent = (event) => {
  if (props.clickAction === 'fireEvent') {
    emit('getMoreDetails', event);
    event.preventDefault();
    return false;
  }
  return true;
};

const openProtectionPlanDetails = async () => {
  await protectionPlanStore.SET_SLIDEOUT({
    id: PROTECTION_PLAN_SLIDER_ID,
    title: `${props.product?.planProviderName || ''} ${PROTECTION_PLAN_SLIDEOUT.title}`,
    props: {
      planDesc: props.product?.planDesc || '',
      orderNumber: props.product?.orderNumber || '',
      serviceLineId: props.product?.serviceLineId || '',
    },
    component: markRaw(defineAsyncComponent(() => import('@/features/ProductThumbnail/components/ProductThumbnail/ProtectionPlanDetails.vue'))),
  });
  slideoutStore.toggle(PROTECTION_PLAN_SLIDER_ID);
};

onUnmounted(() => {
  slideoutStore.close(PROTECTION_PLAN_SLIDER_ID);
});
</script>

<style lang="scss" scoped>
.product-description {
  a {
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  .clickAction {
    cursor: pointer;
  }

  .product-name {
    display: -webkit-box;
    overflow: hidden;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    text-overflow: ellipsis;

    @include breakpoint(small down) {
      -webkit-line-clamp: 3;
    }
  }

  @include breakpoint(medium) {
    .product-name {
      max-width: rem-calc(230);
    }
  }

  .product-size-color,
  .product-qty,
  .product-return-window {
    color: $gray-1-color;
  }

  .product-size-color {
    margin-top: rem-calc(10);
  }

  .expand .pricing {
    &.ps-margin-A {
      margin-top: rem-calc(8);
    }

    &.ps-margin-B {
      margin-top: rem-calc(12);
    }
  }

  .protection-plan {
    &-price {
      font-weight: 700;
    }

    &-status {
      color: $gray-1-color;
    }

    &-provider {
      margin-bottom: 4px;
    }
  }
}

.h3-exp-style {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: 0.14px;
  text-transform: none;
}
</style>

<style lang="scss" brand="mcom" scoped>
.h3-exp-style {
  line-height: 20px;
}
</style>

<style lang="scss" brand="bcom" scoped>
.product-description {
  display: flex;
  flex-direction: column;

  .pricing-and-reviewlet {
    display: flex;
    flex-direction: column;
    order: 2;

    :deep(.rating) {
      order: 1;
    }

    :deep(.pricing) {
      order: 2;
    }

    :deep(.protection-plan) {
      order: 3;
    }
  }
}

.h3-exp-style {
  line-height: 18px;
}
</style>
